import BaseComponent from '../../base/base_component';
import DOM from '../../utils/dom';
import _ from '../../utils/underscore';
import { SYSTEM_EVENTS } from "../../events/events";
import { BaseComponentParams } from "../../base/base_component_interface";
import Device from "../../utils/device/device";

const templateView = require('./view/view.html');

interface NetPositionControllerParams extends BaseComponentParams {
    gameType?: number;
}

export default class NetPositionController extends BaseComponent {
    static CLASS = 'NetPositionController';

    protected _selector = 'cgp-net-position-component';
    protected _valueSelector = `.${this._selector}-value`;

    private params: NetPositionControllerParams;

    private receivedFireBaseValueWithoutRoundEndCounter = 0;

    private FLAGS = {
        FEATURE_ENABLED: false,
        ROUND_END_EXISTS_FOR_GAME: false,
        RECEIVED_ROUND_END: false,
        LAST_TIME_RECEIVED_ROUND_END: null,
        NET_POSITION_LAST_RECEIVED_VALUE: null,
        TOTAL_NET_POSITION_RECEIVED_COUNT: 0
    };

    init(params) {
        params = params || {};
        super.init(params);

        this._bakeHTML();

        this.params = params;

        this._setAmount(0);
        this._resetFirebaseGameTypeForSessionAmount();

        this._subscribe();
    }

    _bakeHTML() {
        this.setHTML(this.bake(templateView));

        if (Device.isMobile()) {
            this.addClass('cgp-themes-small-secondary-grayed-out-text');
        } else {
            this.addClass('cgp-themes-grayed-out-text');
        }
    }

    _setAmount(value) {

        if (_.isNullOrUndefined(value)) {
            value = 0;
        }

        this.logger.debug('Net Position strip update', value, this._getCurrentFlags());

        DOM.text(this.querySelector(this._valueSelector), this.serviceFactory.currencyService().formatMoney(value));

        this._resetQueueValues();

        //Report strip update also to Kibana for better monitoring (only for vip users -avoiding too much reporting)
        const accountData = this.serviceFactory.userService().getAccountData();
        if (accountData && accountData.IsVip) {
            this._sendElasticEvent("Net Position Strip update", "Net Position Strip update", value);
        }
    }

    onGameRoundEnd() {

        this.logger.debug("Net Position gameRoundEnd notification received", this._getCurrentFlags());

        if (!this.FLAGS.ROUND_END_EXISTS_FOR_GAME) {
            this.FLAGS.ROUND_END_EXISTS_FOR_GAME = true;
            this.FLAGS.LAST_TIME_RECEIVED_ROUND_END = (new Date()).getTime();
            this.logger.debug("First gameRoundEnd received for game", this._getCurrentFlags());
            return;
        }

        if (this._isLessThenTimeFromLastEndRound()) {
            this.logger.debug("Net Position gameRoundEnd received too early again", this._getCurrentFlags());
            this.FLAGS.LAST_TIME_RECEIVED_ROUND_END = (new Date()).getTime();
            return;
        }


        this.FLAGS.RECEIVED_ROUND_END = true;
        this.FLAGS.LAST_TIME_RECEIVED_ROUND_END = (new Date()).getTime();
        this._checkIfToSetAmount();
    }

    _receivedNetPosition(netPosition = {}) {
        if (netPosition.hasOwnProperty(this.params?.gameType)) {
            this.FLAGS.NET_POSITION_LAST_RECEIVED_VALUE = netPosition[this.params.gameType];
            this.logger.debug('Firebase Net Position received', this.FLAGS.NET_POSITION_LAST_RECEIVED_VALUE, this._getCurrentFlags());

            this.FLAGS.TOTAL_NET_POSITION_RECEIVED_COUNT++;

            if (this.FLAGS.TOTAL_NET_POSITION_RECEIVED_COUNT === 3) { //1: init 0, 2: spin, 3: second spin
                this.FLAGS.FEATURE_ENABLED = true;
                this.logger.debug('Net Position feature was ENABLED', this._getCurrentFlags());
            }

            this._checkIfToSetAmount();
        }
    }

    _checkIfToSetAmount() {
        const currentNetPositionValue = this.FLAGS.NET_POSITION_LAST_RECEIVED_VALUE;

        // If Feature not active OR Game don't have gameRoundEnd => put value
        if ((!this.FLAGS.FEATURE_ENABLED || !this.FLAGS.ROUND_END_EXISTS_FOR_GAME) && currentNetPositionValue !== null) {
            this.logger.debug(`NetPosition not enabled or no roundEnd received, update stripe amount on Firebase update`, this._getCurrentFlags());

            if (!this.FLAGS.ROUND_END_EXISTS_FOR_GAME) {
                this.receivedFireBaseValueWithoutRoundEndCounter++;
            }

            if (this.receivedFireBaseValueWithoutRoundEndCounter === 6) {
                this._sendElasticEvent("missing_round_end_event", "Missing Round End Event", this.params.gameType);
            }

            return this._setAmount(currentNetPositionValue);
        }

        if (this.FLAGS.RECEIVED_ROUND_END && currentNetPositionValue !== null) {
            this.logger.debug('Both roundEnd and Firebase notification received - update stripe amount', this._getCurrentFlags());
            this._setAmount(currentNetPositionValue);
        }
    }

    _subscribe() {
        this.onSystemEvent(SYSTEM_EVENTS.NET_POSITION_VALUE_UPDATE, this._receivedNetPosition.bind(this));
    }

    _resetFirebaseGameTypeForSessionAmount() {
        this.params?.gameType && this.serviceFactory.netPositionService().initializeNetPositionOnGameType(this.params.gameType);
    }

    _resetQueueValues() {
        this.FLAGS.NET_POSITION_LAST_RECEIVED_VALUE = null;
        this.FLAGS.RECEIVED_ROUND_END = false;

        this.logger.debug('Reset Queue Values', this._getCurrentFlags());
    }

    _sendElasticEvent(action: string, label: string, value: any) {
        const data = {
            CGPEventCategory: "NetPosition",
            CGPEventAction: action,
            CGPEventLabel: label,
            CGPEventValue: value
        };
        this.serviceFactory.structuredLoggingService().getLogger(data).info();
    }

    //time: 500ms
    _isLessThenTimeFromLastEndRound() {
        return Math.floor(new Date().getTime() - this.FLAGS.LAST_TIME_RECEIVED_ROUND_END) < 500;
    }

    _getCurrentFlags() {
        return { ...this.FLAGS };
    }
}
